<template>
  <div>
    <b-overlay :show="isLoading" rounded="sm">
      <b-card no-body>
        <b-card-body>
          <b-row class="mb-1">
            <b-col cols="12" md="4" class="mb-50">
              <b-form-input v-model="search" placeholder="Поиск по названию или теме..." />
            </b-col>
            <b-col cols="12" md="3" class="mb-50">
              <b-form-select v-model="topicFilter" :options="topicOptions" />
            </b-col>
            <b-col cols="12" md="2" class="mb-50">
              <b-form-select v-model="fileFilter" :options="fileFilterOptions" />
            </b-col>
            <b-col cols="12" md="3" class="mb-50 d-flex align-items-center justify-content-md-end">
              <b-button
                v-b-tooltip.hover
                size="sm"
                variant="outline-primary"
                :disabled="isChecking"
                :title="lastCheckedTitle"
                @click="checkFiles"
              >
                <b-spinner v-if="isChecking" small class="mr-50" />
                <feather-icon v-else icon="RefreshCwIcon" size="14" class="mr-50" />
                Проверить файлы
              </b-button>
            </b-col>
          </b-row>
          <b-row class="mb-1">
            <b-col cols="12" class="d-flex align-items-center flex-wrap">
              <b-form-checkbox v-model="hideArchived" switch class="mr-2">
                Скрыть архивные
              </b-form-checkbox>
              <b-form-checkbox v-model="withoutDateOnly" switch class="mr-2">
                Без даты премьеры
              </b-form-checkbox>
              <b-badge
                v-if="missingCount"
                v-b-tooltip.hover
                variant="light-danger"
                class="cursor-pointer"
                title="Показать только их"
                @click="fileFilter = 'missing'"
              >
                Файл не найден: {{ missingCount }}
              </b-badge>
              <small class="text-muted ml-auto">Показано: {{ filteredPacks.length }} из {{ packs.length }}</small>
            </b-col>
          </b-row>

          <vue-good-table
            :rows="filteredPacks"
            :columns="columns"
            :pagination-options="{ enabled: true, perPage: 50, perPageDropdown: [25, 50, 100] }"
            :sort-options="{ enabled: false }"
          >
            <template slot="emptystate">
              <div class="text-center text-muted py-2">Пакеты не найдены</div>
            </template>
            <template slot="table-row" slot-scope="props">
              <a
                v-if="props.column.field === 'key'"
                v-b-tooltip.hover
                :href="planfixPackUrl(props.row.key)"
                target="_blank"
                rel="noopener"
                title="Открыть пакет в Планфиксе"
                class="text-nowrap"
              >
                {{ props.row.key }}
                <feather-icon icon="ExternalLinkIcon" size="12" />
              </a>
              <span v-else-if="props.column.field === 'name'">
                {{ props.row.name || '—' }}
                <b-badge v-if="props.row.is_archived" variant="light-secondary" class="ml-50">архив</b-badge>
              </span>
              <span v-else-if="props.column.field === 'topic'" class="text-nowrap">
                <span
                  v-if="props.row.topic_cluster"
                  v-b-tooltip.hover
                  :title="props.row.topic_cluster"
                  class="mr-50"
                >{{ clusterIcon(props.row.topic_cluster) }}</span>{{ props.row.topic || '—' }}
              </span>
              <span
                v-else-if="balanceFields.includes(props.column.field)"
                class="d-inline-block w-100 text-center text-nowrap"
              >
                <span v-if="props.row[props.column.field]">{{ props.row[props.column.field] }}</span>
                <span v-else class="text-muted">—</span>
              </span>
              <span v-else-if="props.column.field === 'premiere_date'">{{ formatDate(props.row.premiere_date) }}</span>
              <span v-else-if="props.column.field === 'file_path'" class="d-inline-block w-100 text-center">
                <feather-icon
                  v-if="isFileMissing(props.row)"
                  v-b-tooltip.hover
                  icon="AlertTriangleIcon"
                  size="18"
                  class="text-danger"
                  :title="fileTitle(props.row)"
                />
                <feather-icon
                  v-else-if="props.row.file_path"
                  v-b-tooltip.hover
                  icon="PaperclipIcon"
                  size="18"
                  class="text-success"
                  :title="fileTitle(props.row)"
                />
                <span v-else class="text-muted">—</span>
              </span>
              <span v-else-if="props.column.field === 'actions'" class="text-nowrap">
                <b-button
                  v-b-tooltip.hover
                  size="sm"
                  variant="outline-primary"
                  class="btn-icon"
                  title="Выбрать файл с Яндекс.Диска"
                  @click="openDiskPicker(props.row)"
                >
                  <feather-icon icon="SearchIcon" size="14" />
                </b-button>
                <b-button
                  v-if="props.row.file_path"
                  v-b-tooltip.hover
                  size="sm"
                  variant="outline-danger"
                  class="btn-icon ml-50"
                  title="Открепить файл"
                  :disabled="savingKey === props.row.key"
                  @click="clearFile(props.row)"
                >
                  <feather-icon icon="XIcon" size="14" />
                </b-button>
                <b-button
                  v-if="props.row.file_path && props.row.id && $can('manage', 'questions')"
                  v-b-tooltip.hover
                  size="sm"
                  variant="outline-success"
                  class="ml-50"
                  title="Импортировать пакет в хранилище вопросов"
                  :to="{ name: 'question-storage-import', query: { planfix_pack_id: props.row.id } }"
                >
                  В хранилище
                </b-button>
              </span>
              <span v-else>{{ props.formattedRow[props.column.field] }}</span>
            </template>
          </vue-good-table>
        </b-card-body>
      </b-card>
    </b-overlay>

    <b-modal
      v-model="diskModalVisible"
      :title="diskModalTitle"
      size="lg"
      hide-footer
    >
      <div class="mb-1 d-flex align-items-center flex-wrap">
        <b-button
          size="sm"
          variant="flat-secondary"
          :disabled="diskPath === 'disk:/' || isDiskLoading"
          @click="goUp"
        >
          ← Вверх
        </b-button>
        <code class="ml-1">{{ diskPath }}</code>
      </div>

      <b-overlay :show="isDiskLoading" rounded="sm">
        <b-list-group style="max-height: 60vh; overflow-y: auto;">
          <b-list-group-item
            v-for="item in diskItems"
            :key="item.path"
            :class="{ 'cursor-pointer': isClickable(item) }"
            :disabled="!isClickable(item)"
            button
            @click="onDiskItemClick(item)"
          >
            <span v-if="item.type === 'dir'">📁 {{ item.name }}</span>
            <span v-else>
              📦 {{ item.name }}
              <small class="text-muted ml-50">{{ formatBytes(item.size) }}</small>
            </span>
          </b-list-group-item>
          <b-list-group-item v-if="!isDiskLoading && diskItems.length === 0" class="text-center text-muted">
            Папка пуста
          </b-list-group-item>
        </b-list-group>
      </b-overlay>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BCardBody, BRow, BCol, BFormInput, BFormSelect, BFormCheckbox, BButton, BOverlay, BModal, BBadge,
  BListGroup, BListGroupItem, BSpinner, VBTooltip,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import { ref, computed, onUnmounted } from '@vue/composition-api'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import gameStoreModule from '@/views/game/gameStoreModule'
import { planfixPackUrl } from '@/views/finance/utils'

const STORE_MODULE_NAME = 'app-games'

export default {
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BFormSelect,
    BFormCheckbox,
    BButton,
    BOverlay,
    BModal,
    BBadge,
    BListGroup,
    BListGroupItem,
    BSpinner,
    VueGoodTable,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  setup(props, { root }) {
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, gameStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const toast = useToast()
    const showError = title => toast({
      component: ToastificationContent,
      props: { title, icon: 'AlertTriangleIcon', variant: 'danger' },
    })
    const showSuccess = title => toast({
      component: ToastificationContent,
      props: { title, icon: 'CheckIcon', variant: 'success' },
    })

    const packs = ref([])
    const isLoading = ref(false)
    const search = ref('')
    const hideArchived = ref(true)
    const withoutDateOnly = ref(false)
    const topicFilter = ref(null)
    const fileFilter = ref(null)
    const savingKey = ref(null)
    const lastCheckedAt = ref(null)
    const isChecking = ref(false)

    const FILE_STATUS_MISSING = 'missing'
    const isFileMissing = pack => Boolean(pack.file_path) && pack.file_status === FILE_STATUS_MISSING

    const fileFilterOptions = [
      { value: null, text: 'Файл: все' },
      { value: 'with', text: 'С файлом' },
      { value: 'without', text: 'Без файла' },
      { value: 'missing', text: 'Файл не найден' },
    ]

    const topicOptions = computed(() => {
      const topics = [...new Set(packs.value.map(pack => pack.topic).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b, 'ru'))

      return [
        { value: null, text: 'Все темы' },
        ...topics.map(topic => ({ value: topic, text: topic })),
      ]
    })

    const missingCount = computed(() => packs.value.filter(isFileMissing).length)

    const columns = [
      { label: 'ID', field: 'key', width: '90px' },
      { label: 'Название', field: 'name' },
      { label: 'Тема', field: 'topic' },
      { label: 'RU/EN', field: 'balance_ru_en', width: '80px', thClass: 'text-center' },
      { label: 'Рок/Нерок', field: 'balance_rock', width: '95px', thClass: 'text-center' },
      { label: 'XX/XXI', field: 'balance_century', width: '80px', thClass: 'text-center' },
      { label: 'Премьера', field: 'premiere_date', width: '110px' },
      { label: 'Файл', field: 'file_path', width: '70px', tdClass: 'text-center', thClass: 'text-center' },
      { label: '', field: 'actions', width: '220px' },
    ]

    const balanceFields = ['balance_ru_en', 'balance_rock', 'balance_century']

    // Кластер тематики из справочника Planfix: «Музыка» / «Кино» / «Другое»
    const CLUSTER_ICONS = {
      Музыка: '🎵',
      Кино: '🎬',
    }
    const clusterIcon = cluster => CLUSTER_ICONS[cluster] || '🧩'

    const filteredPacks = computed(() => {
      const query = search.value.trim().toLowerCase()

      return packs.value.filter(pack => {
        if (hideArchived.value && pack.is_archived && !pack.file_path) return false
        if (withoutDateOnly.value && pack.premiere_date) return false
        if (topicFilter.value && pack.topic !== topicFilter.value) return false
        if (fileFilter.value === 'with' && !pack.file_path) return false
        if (fileFilter.value === 'without' && pack.file_path) return false
        if (fileFilter.value === 'missing' && !isFileMissing(pack)) return false
        if (!query) return true

        return (pack.name || '').toLowerCase().includes(query)
          || (pack.topic || '').toLowerCase().includes(query)
          || String(pack.key).includes(query)
      })
    })

    const fetchPacks = () => {
      isLoading.value = true
      store.dispatch('app-games/fetchPlanfixPacks')
        .then(response => {
          packs.value = response.data.packs
          lastCheckedAt.value = response.data.last_checked_at
        })
        .catch(() => showError('Не удалось загрузить пакеты'))
        .finally(() => {
          isLoading.value = false
        })
    }

    const formatDateTime = value => new Date(value).toLocaleString('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    })

    const lastCheckedTitle = computed(() => (lastCheckedAt.value
      ? `Последняя проверка: ${formatDateTime(lastCheckedAt.value)}`
      : 'Файлы ещё не проверялись'))

    const fileTitle = pack => {
      const checkedAt = pack.file_checked_at ? ` (проверено ${formatDateTime(pack.file_checked_at)})` : ''

      return isFileMissing(pack)
        ? `Файл не найден — переименован или удалён: ${pack.file_path}${checkedAt}`
        : `${pack.file_path}${checkedAt}`
    }

    // Проверка идёт синхронно (~15 с: листинг папок Диска параллельно), затем список перечитывается
    const checkFiles = () => {
      isChecking.value = true
      store.dispatch('app-games/checkPlanfixPackFiles')
        .then(response => {
          const { found, missing, failed } = response.data
          const failedText = failed ? `, не удалось проверить: ${failed}` : ''
          if (missing) {
            showError(`Не найдено файлов: ${missing} (на месте: ${found}${failedText})`)
          } else {
            showSuccess(`Все файлы на месте: ${found}${failedText}`)
          }
          fetchPacks()
        })
        .catch(() => showError('Не удалось проверить файлы'))
        .finally(() => {
          isChecking.value = false
        })
    }

    // --- Браузер Яндекс.Диска ---
    const diskModalVisible = ref(false)
    // Пустой путь: первый запрос уходит без path, бэкенд открывает папку пакетов из конфига
    const diskPath = ref('')
    const diskItems = ref([])
    const isDiskLoading = ref(false)
    const selectedPack = ref(null)

    const diskModalTitle = computed(() => (selectedPack.value ? `Файл для «${selectedPack.value.name}»` : 'Яндекс.Диск'))

    const loadDisk = path => {
      isDiskLoading.value = true
      store.dispatch('app-games/fetchPlanfixPacksDisk', { path })
        .then(response => {
          diskPath.value = response.data.path
          diskItems.value = response.data.items
        })
        .catch(error => showError((error.response && error.response.data && error.response.data.message) || 'Не удалось открыть папку Диска'))
        .finally(() => {
          isDiskLoading.value = false
        })
    }

    const openDiskPicker = pack => {
      selectedPack.value = pack
      diskModalVisible.value = true
      loadDisk(diskPath.value || null)
    }

    const goUp = () => {
      const withoutPrefix = diskPath.value.replace(/^disk:\//, '')
      const segments = withoutPrefix.split('/').filter(Boolean)
      segments.pop()
      loadDisk(`disk:/${segments.join('/')}`)
    }

    const isSiqFile = item => item.type === 'file' && item.name.toLowerCase().endsWith('.siq')
    const isClickable = item => item.type === 'dir' || isSiqFile(item)

    const saveFile = (pack, filePath) => {
      savingKey.value = pack.key

      return store.dispatch('app-games/savePlanfixPackFile', { key: pack.key, filePath })
        .then(response => {
          const row = packs.value.find(p => p.key === pack.key)
          if (row) Object.assign(row, response.data.pack)
          showSuccess(filePath ? 'Файл привязан к пакету' : 'Файл откреплён')
        })
        .catch(() => showError('Не удалось сохранить файл пакета'))
        .finally(() => {
          savingKey.value = null
        })
    }

    const onDiskItemClick = item => {
      if (item.type === 'dir') {
        loadDisk(item.path)
        return
      }
      if (!isSiqFile(item)) return

      saveFile(selectedPack.value, item.path).then(() => {
        diskModalVisible.value = false
      })
    }

    const clearFile = async pack => {
      const confirmed = await root.$bvModal.msgBoxConfirm(
        `Открепить файл от пакета «${pack.name}»?`,
        {
          title: 'Открепление файла',
          okTitle: 'Открепить',
          cancelTitle: 'Отмена',
          okVariant: 'danger',
          centered: true,
        },
      )
      if (!confirmed) return

      saveFile(pack, null)
    }

    const formatDate = value => {
      if (!value) return '—'
      const [year, month, day] = value.split('-')

      return `${day}.${month}.${year}`
    }

    const formatBytes = size => {
      if (size == null) return ''
      if (size < 1024 * 1024) return `${Math.round(size / 1024)} КБ`

      return `${(size / (1024 * 1024)).toFixed(1)} МБ`
    }

    fetchPacks()

    return {
      packs,
      filteredPacks,
      isLoading,
      search,
      hideArchived,
      withoutDateOnly,
      topicFilter,
      topicOptions,
      fileFilter,
      fileFilterOptions,
      missingCount,
      isFileMissing,
      fileTitle,
      isChecking,
      lastCheckedTitle,
      checkFiles,
      savingKey,
      columns,
      balanceFields,
      clusterIcon,
      planfixPackUrl,
      diskModalVisible,
      diskModalTitle,
      diskPath,
      diskItems,
      isDiskLoading,
      openDiskPicker,
      onDiskItemClick,
      isClickable,
      goUp,
      clearFile,
      formatDate,
      formatBytes,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
